import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Info, FileDown } from "lucide-react";

const fmtD = (iso) => { if (!iso) return "—"; const p = String(iso).slice(0, 10).split("-"); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const fmtDT = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; } };
const clean = (s) => String(s || "").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
const parseCommessa = (raw) => {
  const s = clean(raw);
  if (!s) return null;
  const grab = (start, ends) => {
    const i = s.indexOf(start);
    if (i < 0) return "";
    const from = i + start.length;
    let end = s.length;
    for (const e of ends) { const j = s.indexOf(e, from); if (j >= 0 && j < end) end = j; }
    return s.slice(from, end).trim();
  };
  const cliente = grab("Cliente:", ["Conferma d'Ordine:", "Richiesta:", "Scheda Commessa"]);
  const ordine = grab("Conferma d'Ordine:", ["Richiesta:", "Scheda Commessa"]);
  const richiesta = grab("Richiesta:", ["Scheda Commessa"]);
  if (!cliente && !ordine && !richiesta) return null;
  return { cliente, ordine, richiesta };
};
const DRow = ({ label, value }) => value ? <div className="leading-snug"><span className="text-muted-foreground">{label} </span><span className="text-foreground">{value}</span></div> : null;
const hasOrdRef = (s) => /(c\.o\.|conferma d'ordine|\bco\s+\d)/i.test(clean(s));
const ManualBadge = () => <Badge variant="outline" className="border-amber-500/50 text-amber-600 dark:text-amber-400" data-testid="mag-manual-badge">Inserimento manuale</Badge>;
const TimelineDetail = ({ e }) => {
  if (e.tipo === "giacenza") return <span className="text-muted-foreground">Giacenza di partenza (oggi)</span>;
  if (e.tipo === "scarico") {
    const c = parseCommessa(e.commessa) || {};
    const cliente = e.cliente || c.cliente;
    const ordine = e.ordine || c.ordine;
    const causale = clean(e.causale);
    const manuale = !cliente && !ordine && !hasOrdRef(e.causale);
    return (
      <div className="space-y-0.5">
        {manuale && <ManualBadge />}
        <DRow label="Cliente:" value={cliente} />
        <DRow label="C.O.:" value={ordine} />
        <DRow label="Richiesta:" value={c.richiesta} />
        {(cliente || ordine) && causale && causale !== "Ordine" && <DRow label="Documento:" value={causale} />}
        {(!cliente && !ordine) && <DRow label="Causale:" value={causale} />}
        <DRow label="Stato:" value={e.stato} />
      </div>
    );
  }
  const manuale = !e.fornitore && !hasOrdRef(e.causale);
  return (
    <div className="space-y-0.5">
      {manuale && <ManualBadge />}
      <DRow label="Fornitore:" value={e.fornitore} />
      <DRow label="Ordine:" value={clean(e.causale)} />
      {e.in_ritardo && e.data_originale && <DRow label="Consegna:" value={`orig. ${fmtD(e.data_originale)} → ${fmtD(e.data_ritardo || e.data)}`} />}
      <DRow label="Stato:" value={e.stato} />
    </div>
  );
};

const Box = ({ label, value, tone }) => {
  const c = tone === "red" ? "text-red-600 dark:text-red-400" : tone === "emerald" ? "text-emerald-600 dark:text-emerald-400" : tone === "primary" ? "text-primary" : "text-foreground";
  return (
    <div className="rounded-lg border border-input bg-muted/30 p-2.5">
      <div className="text-[11px] leading-tight text-muted-foreground">{label}</div>
      <div className={`text-lg font-black ${c}`}>{value}</div>
    </div>
  );
};

export const MagazzinoDetailDialog = ({ open, onOpenChange, codice, quantita = 0, dataRichiesta = null, preventivi = null }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  useEffect(() => {
    if (!open || !codice) return;
    setLoading(true); setData(null);
    api.post("/magazzino/detail", { codice, quantita, data_richiesta: dataRichiesta })
      .then((r) => setData(r.data)).catch(() => setData({ error: true })).finally(() => setLoading(false));
  }, [open, codice, quantita, dataRichiesta]);

  const contesto = useMemo(() => {
    if (!codice || !preventivi) return [];
    const cl = String(codice).trim().toLowerCase();
    const out = [];
    for (const p of preventivi) {
      const c = (p.critici || []).find((x) => (x.codice || "").trim().toLowerCase() === cl);
      if (c) out.push({ ...p, mancante: c.mancante });
    }
    return out;
  }, [codice, preventivi]);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.post("/magazzino/detail/pdf", { codice, quantita, data_richiesta: dataRichiesta }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `movimenti_${String(codice).replace(/[^A-Za-z0-9_-]+/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Stampa movimenti non riuscita"); } finally { setPdfBusy(false); }
  };

  const canPdf = data && !loading && !data.error && data.tracciato !== false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mag-detail-dialog">
        <DialogHeader>
          <div className="flex flex-wrap items-center justify-between gap-2 pr-8">
            <DialogTitle className="flex items-center gap-2 text-base"><Info className="h-4 w-4 text-primary" /> Dettaglio disponibilità · <span className="font-mono">{codice}</span></DialogTitle>
            {canPdf && (
              <Button variant="outline" size="sm" onClick={downloadPdf} disabled={pdfBusy} data-testid="mag-detail-pdf-btn">
                {pdfBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileDown className="mr-1 h-4 w-4" />} Stampa movimenti (PDF)
              </Button>
            )}
          </div>
        </DialogHeader>
        {loading ? <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carico i movimenti…</div>
          : !data ? null
            : data.error ? <div className="p-4 text-sm text-red-500" data-testid="mag-detail-error">Errore nel recupero dei dati.</div>
              : data.tracciato === false ? <div className="p-4 text-sm text-muted-foreground" data-testid="mag-detail-untracked">Articolo non tracciato a magazzino: nessun dato di giacenza/movimenti disponibile.</div>
                : (
                  <div className="space-y-4 text-sm">
                    {data.da_documenti && (
                      <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-400" data-testid="mag-detail-da-documenti">
                        Articolo non presente a magazzino: le uscite mostrate sono ricostruite dagli ordini e preventivi che lo richiedono (giacenza non disponibile).
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-4" data-testid="mag-detail-summary">
                      <Box label="Giacenza attuale" value={data.da_documenti ? "n/d" : data.giacenza} />
                      <Box label={`Uscite entro ${data.data_richiesta ? fmtD(data.data_richiesta) : "richiesta n/d"}`} value={data.data_richiesta ? data.uscite_entro : "—"} tone="red" />
                      <Box label={`Arrivi entro ${data.data_richiesta ? fmtD(data.data_richiesta) : "richiesta n/d"}`} value={data.data_richiesta ? data.arrivi_entro : "—"} tone="emerald" />
                      <Box label="Saldo alla data richiesta" value={data.saldo_alla_data == null ? "—" : data.saldo_alla_data} tone="primary" />
                    </div>
                    {contesto.length > 0 && (
                      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="mag-detail-contesto">
                        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary">Preventivi che generano lo scoperto ({contesto.length})</div>
                        <div className="max-h-40 space-y-1.5 overflow-y-auto">
                          {contesto.map((p, i) => (
                            <div key={i} className="flex items-start justify-between gap-2 border-t border-border/40 pt-1.5 text-xs first:border-0 first:pt-0" data-testid={`mag-detail-prev-${i}`}>
                              <div className="min-w-0">
                                <div className="truncate font-medium">{p.cliente || "—"}</div>
                                <div className="text-[10px] text-muted-foreground">
                                  {[p.numero && `N° ${p.numero}`, p.stato, p.source === "crm" ? "Preventivatore" : "Gestionale"].filter(Boolean).join(" · ")}
                                  {p.data_richiesta ? ` · richiesta ${fmtD(p.data_richiesta)}` : (p.date ? ` · ${fmtD(p.date)}` : "")}
                                </div>
                              </div>
                              <div className="shrink-0 font-mono font-bold text-red-600 dark:text-red-400">{p.mancante} pz</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="outline">Flusso: {data.flusso || "—"}</Badge>
                      <Badge variant="outline">ATP disponibile subito: {data.esito?.disponibile_ora ?? 0}</Badge>
                      {data.quantita > 0 && data.esito?.scoperto > 0 && <Badge variant="destructive">Scoperto: {data.esito.scoperto}</Badge>}
                      <span>Aggiornato: {fmtDT(data.updated_at)}</span>
                    </div>
                    <div>
                      <div className="mb-1 font-semibold">Proiezione cronologica (saldo progressivo)</div>
                      <div className="overflow-x-auto rounded-lg border border-input">
                        <table className="w-full text-xs" data-testid="mag-detail-timeline">
                          <thead className="bg-muted"><tr>
                            <th className="p-2 text-left font-bold">Data</th>
                            <th className="p-2 text-left font-bold">Movimento</th>
                            <th className="p-2 text-right font-bold">Q.tà</th>
                            <th className="p-2 text-right font-bold">Saldo</th>
                            <th className="p-2 text-left font-bold">Dettaglio (cliente / fornitore)</th>
                          </tr></thead>
                          <tbody>
                            {(data.timeline || []).map((e, i) => {
                              const oltre = data.data_richiesta && e.data_effettiva && e.data_effettiva > data.data_richiesta;
                              return (
                                <tr key={i} className={`border-t border-input/60 align-top ${oltre ? "opacity-45" : ""}`} data-testid={`mag-detail-row-${i}`}>
                                  <td className="p-2 whitespace-nowrap">{e.tipo === "giacenza" ? "oggi" : fmtD(e.data || e.data_effettiva)}{e.in_ritardo ? <span className="ml-1 text-amber-500">(ritardo)</span> : ""}</td>
                                  <td className="p-2 whitespace-nowrap">{e.tipo === "giacenza" ? "Giacenza attuale" : e.tipo === "carico" ? "Carico (arrivo)" : "Scarico (impegno)"}</td>
                                  <td className={`p-2 text-right font-semibold ${e.tipo === "scarico" ? "text-red-500" : e.tipo === "carico" ? "text-emerald-600" : ""}`}>{e.tipo === "giacenza" ? e.qta : (e.qta > 0 ? `+${e.qta}` : e.qta)}</td>
                                  <td className="p-2 text-right font-bold">{e.saldo}</td>
                                  <td className="p-2"><TimelineDetail e={e} /></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {data.data_richiesta && <div className="mt-1 text-[11px] text-muted-foreground">Le righe in grigio sono successive alla data richiesta dal cliente ({fmtD(data.data_richiesta)}) e non incidono sul saldo a quella data.</div>}
                    </div>
                  </div>
                )}
      </DialogContent>
    </Dialog>
  );
};

export default MagazzinoDetailDialog;
